// frontend/src/shared/utils/offlineManager.ts
import localforage from 'localforage';
import { SubmitInspectionPayload } from '@core/types';

// Configuración de almacenamiento offline para inspecciones pendientes
const pendingSyncStore = localforage.createInstance({
  name: 'pending_sync',
  storeName: 'pending',
});

export interface PendingInspection {
  id: string;
  payload: SubmitInspectionPayload;
  timestamp: Date;
  retries: number;
}

export const offlineManager = {
  /**
   * Guarda una inspección pendiente en la cola offline
   */
  async addPendingInspection(payload: SubmitInspectionPayload): Promise<string> {
    const id = `pending_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const pending: PendingInspection = {
      id,
      payload,
      timestamp: new Date(),
      retries: 0,
    };
    await pendingSyncStore.setItem(id, pending);
    return id;
  },

  /**
   * Obtiene todas las inspecciones pendientes
   */
  async getPendingInspections(): Promise<PendingInspection[]> {
    const keys = await pendingSyncStore.keys();
    if (!keys || keys.length === 0) {
      return [];
    }
    const items = await Promise.all(
      keys.map(async (key) => {
        const item = await pendingSyncStore.getItem<PendingInspection>(key);
        return item;
      })
    );
    const validItems = items.filter((item): item is PendingInspection => item !== null);
    // Ordenar por timestamp (más antiguos primero)
    return validItems.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  },

  /**
   * Elimina una inspección pendiente (después de sincronizar)
   */
  async removePendingInspection(id: string): Promise<void> {
    await pendingSyncStore.removeItem(id);
  },

  /**
   * Limpia todas las inspecciones pendientes (útil para tests)
   */
  async clearPendingInspections(): Promise<void> {
    await pendingSyncStore.clear();
  },

  /**
   * Incrementa el contador de reintentos
   */
  async incrementRetryCount(id: string): Promise<void> {
    const item = await pendingSyncStore.getItem<PendingInspection>(id);
    if (item) {
      item.retries += 1;
      await pendingSyncStore.setItem(id, item);
    }
  },

  /**
   * Obtiene el número de inspecciones pendientes
   */
  async getPendingCount(): Promise<number> {
    const keys = await pendingSyncStore.keys();
    return keys?.length || 0;
  },
};